import { create } from 'zustand';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import { PlannedArrivalDate, Task } from '../../api-client/services/v1/TaskService/interfaces';
import serviceInitializer from '../../api-client/configs/serviceInitializer';
import { TaskService } from '../../api-client/services/v1/TaskService';
import { defaultIndicators, updateIndicators, StatusIndicators } from '../indicators';
import { sendSentryData } from '../../../configs/sendSentryData';
import { AsyncState, AsyncStatus, toAppError } from '../../../types';

export interface TaskStoreData {
    taskState: AsyncState<Task>;
    arrivalDate: PlannedArrivalDate;
    arrivalDateIndicators: StatusIndicators;
    getTask: (id: number) => Promise<void>;
    getPlannedArrivalDate: (id: number) => Promise<void>;
    getTaskInfo: (id: number) => Promise<void>;
}

export const useTaskStore = create<TaskStoreData>((set, get) => {
    const taskServices = serviceInitializer<TaskService>(TaskService);

    const getTask = async (id: number) => {
        set({ taskState: { status: AsyncStatus.LOADING } });
        try {
            const task = await taskServices.getTask({ id });
            set({ taskState: { status: AsyncStatus.SUCCESS, data: task } });
        } catch (error) {
            console.error(error);
            set({ taskState: { status: AsyncStatus.ERROR, error: toAppError(error) } });
            sendSentryData({
                message: `Failed to get task ${id}`,
            });
        }
    };

    const getPlannedArrivalDate = async (id: number) => {
        set({
            arrivalDateIndicators: updateIndicators(get().arrivalDateIndicators, {
                isLoading: true,
            }),
        });
        try {
            const arrivalDate = await taskServices.getPlannedArrivalDate({ id });
            const formattedArrivalDate = arrivalDate
                ? {
                      ...arrivalDate,
                      dateTime: format(new Date(arrivalDate.dateTime), 'dd MMM yyyy, HH:mm', {
                          locale: ru,
                      }),
                  }
                : null;
            set({
                arrivalDate: formattedArrivalDate,
                arrivalDateIndicators: updateIndicators(get().arrivalDateIndicators, {
                    isLoading: false,
                    isEmpty: !arrivalDate,
                    isError: false,
                }),
            });
        } catch (error) {
            console.error(error);
            set({
                arrivalDateIndicators: updateIndicators(get().arrivalDateIndicators, {
                    isError: true,
                    isEmpty: true,
                }),
            });
            sendSentryData({
                message: `Failed to get arrival date of task ${id}`,
            });
        } finally {
            set({
                arrivalDateIndicators: updateIndicators(get().arrivalDateIndicators, {
                    isLoading: false,
                }),
            });
        }
    };

    const getTaskInfo = async (id: number) => {
        await getTask(id);
        await getPlannedArrivalDate(id);
    };

    return {
        taskState: { status: AsyncStatus.IDLE },
        arrivalDateIndicators: defaultIndicators,
        arrivalDate: null,
        getTask,
        getPlannedArrivalDate,
        getTaskInfo,
    };
});
